import { NextApiRequest, NextApiResponse } from 'next';
import { NextApiRequestQuery } from 'next/dist/server/api-utils';
import { SSRConfig } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
    getDisciplineCookieFromRequest,
    getTokenCookieFromContext,
} from '../cookies';
import { getData, getDataData } from '../serverSideData';
import { getUrlWithDiscipline } from './utils/config';
import { stripUndefined } from 'utils/helpers/stripUndefined';
import { Client } from 'submodules/api_middleware';
import { MenuApiEnum } from 'components/interfaces/GeneralInterface';
import { LangEnum } from 'types/types';
import { TokenPurpose } from 'utils/helpers/cookies.shared';
import { isDelegate, isReferee } from 'utils/helpers/userRoles.shared';
// TODO: po otestování na produkci smazat – dočasná viditelnost Databáze trenérů.
import { isCoachesRequestAllowed } from 'utils/helpers/server/coachesAccess';

export interface ViewerRoleFlags {
    isReferee: boolean;
    isDelegate: boolean;
}

export const EMPTY_VIEWER_ROLE_FLAGS: ViewerRoleFlags = {
    isReferee: false,
    isDelegate: false,
};

interface PagePropsType<T> {
    givenProps: T;
    nameSpaces: string[];
    res?: NextApiResponse;
    req?: NextApiRequest;
    api?: Client;
    query?: NextApiRequestQuery;
    loadUserData?: boolean;
    loadMenuData?: boolean;
    translatePage?: boolean;
}

export const getPageProps = async <T>({
    givenProps,
    nameSpaces,
    res,
    req,
    api,
    query,
    loadUserData = true,
    loadMenuData = true,
    translatePage = false,
}: PagePropsType<T>): Promise<T> => {
    let user = null;
    let menu: MenuApiEnum[] = [];
    let viewerRoleFlags: ViewerRoleFlags = EMPTY_VIEWER_ROLE_FLAGS;
    // TODO: po otestování na produkci smazat – dočasná viditelnost Databáze trenérů.
    let coachesAccessible = false;

    if (req && res && api) {
        const token = getTokenCookieFromContext(req, res, TokenPurpose.Refresh);
        const isNotLogoutPage = !req.url.startsWith('/logout');

        user =
            loadUserData && isNotLogoutPage && token
                ? await getDataData(res, req, api.users().me())
                : null;

        menu =
            loadMenuData && query
                ? (await getData<MenuApiEnum[]>(
                      res,
                      req,
                      api.config().get(getUrlWithDiscipline(req, query, 'menu'))
                  )) || []
                : [];

        const accessToken = getTokenCookieFromContext(
            req,
            res,
            TokenPurpose.Access
        );
        const discipline = getDisciplineCookieFromRequest(req, query ?? {});
        viewerRoleFlags = {
            isReferee: isReferee(accessToken, discipline),
            isDelegate: isDelegate(accessToken, discipline),
        };
        // TODO: po otestování na produkci smazat.
        coachesAccessible = isCoachesRequestAllowed(req, res);
    }

    const lang =
        translatePage && req.cookies.lang ? req.cookies.lang : LangEnum.cs;

    const props: T = {
        ...givenProps,
        ...(await getServerSideTransaltions(lang, nameSpaces)),
        nameSpaces,
        lang,
        user,
        menu,
        viewerRoleFlags,
        // TODO: po otestování na produkci smazat.
        coachesAccessible,
    };

    stripUndefined(props);
    return props;
};

export const getServerSideTransaltions = async (
    locale: string,
    nameSpaces: string[] = []
): Promise<SSRConfig> => await serverSideTranslations(locale, nameSpaces);
